//@flow
import React from 'react';
import {Redirect, useHistory} from 'react-router-dom';

import {
  makeStyles,
  Typography,
  IconButton,
  ClickAwayListener,
  Tooltip,
  Button,
} from '@material-ui/core';
import {ArrowBack, CheckCircle} from '@material-ui/icons';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import {useToggle} from '../../../utils/react';
import {colors} from '../../../assets/styles/colors';
import {useEffect} from 'react';

type Props = {
  createOrderSuccess: boolean,
  order: any,
  resetState: () => void,
  orderLink: string,
};

const useStyles = makeStyles(theme => ({
  headerWrapper: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(4, 4, 0, 4),
    background: '#f8f9fb',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3, 4, 0, 4),
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(3, 2, 0, 2),
    },
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'black',
  },
  backButton: {
    background: 'white',
    padding: '4px',
    margin: '0 16px 0 0',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  content: {
    display: 'flex',
    alignItems: 'flex-start',
    maxWidth: 1300,
    gap: theme.spacing(2),
    padding: theme.spacing(0, 2, 4, 2),
    [theme.breakpoints.only('sm')]: {
      marginBottom: 64,
    },
    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(0, 4, 4, 4),
    },
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'center',
      padding: theme.spacing(0, 1, 2, 1),
      marginBottom: 180,
    },
  },
  successContent: {
    marginTop: theme.spacing(2),
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    padding: theme.spacing(3),
    background: 'white',
  },
  successIcon: {
    marginTop: theme.spacing(2),
    color: colors.success,
  },
  successTitle: {
    marginTop: theme.spacing(3),
    textAlign: 'center',
    fontWeight: 600,
  },
  successMessage: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
  },
  successButton: {
    marginTop: theme.spacing(4),
    width: 240,
  },
  successReturnButton: {
    margin: theme.spacing(1, 0),
  },
  wrapperLink: {
    margin: theme.spacing(2, 0),
    padding: theme.spacing(0, 1),
    width: 'auto',
    maxWidth: '100%',
    background: '#F5F0FF',
    border: '1.25px solid rgba(123, 57, 255, 0.2)',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  containerCopyLink: {
    width: '100%',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',

    cursor: 'default',
    fontSize: '12px',
    color: '#3A3A49',
  },
  containerButtonLink: {
    width: '60px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  copyLink: {
    color: '#500BDB',
  },
}));

export default function OrderCreationSuccess(props: Props) {
  const {createOrderSuccess, order, resetState, orderLink} = props;

  const classes = useStyles();
  const history = useHistory();

  const [openToolTip, toggleOpenTooltip, setOpenToolTip] = useToggle(false);

  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  const closeTooltip = () => {
    setOpenToolTip(false);
  };

  return (
    <>
      {(!createOrderSuccess || !order) && (
        <Redirect to="/orders/manual-order" />
      )}
      <div className={classes.headerWrapper}>
        <Typography className={classes.title}>
          <IconButton
            className={classes.backButton}
            onClick={() => {
              resetState();
              history.push(`/orders/manual-order`);
            }}
          >
            <ArrowBack />
          </IconButton>
          Crear Pedido
        </Typography>
      </div>
      <main className={classes.content}>
        <div className={classes.successContent}>
          <CheckCircle fontSize="large" className={classes.successIcon} />
          <Typography className={classes.successTitle}>
            Creado existosamente
          </Typography>
          <Typography className={classes.successMessage}>
            El pedido fue generado
          </Typography>
          {order && (
            <CustomButton
              variant="contained"
              color="primary"
              className={classes.successButton}
              onClick={() => history.push(`/orders/${order.id}`)}
            >
              Ver pedido
            </CustomButton>
          )}
          {order && orderLink && (
            <div className={classes.wrapperLink}>
              <div className={classes.containerCopyLink}>{orderLink}</div>
              <div className={classes.containerButtonLink}>
                <ClickAwayListener onClickAway={closeTooltip}>
                  <Tooltip
                    PopperProps={{
                      disablePortal: true,
                    }}
                    onClose={closeTooltip}
                    open={openToolTip}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    leaveDelay={300}
                    title="Enlace copiado"
                  >
                    <Button
                      className={classes.copyLink}
                      onClick={() => {
                        navigator &&
                          navigator.clipboard &&
                          navigator.clipboard
                            .writeText(orderLink)
                            .then(() => {
                              toggleOpenTooltip();
                            })
                            .catch(e => console.error(e));
                      }}
                    >
                      Copiar
                    </Button>
                  </Tooltip>
                </ClickAwayListener>
              </div>
            </div>
          )}
          <CustomButton
            // variant="contained"
            color="primary"
            className={classes.successReturnButton}
            startIcon={<ArrowBack />}
            onClick={() => {
              resetState();
              history.push(`/orders/manual-order`);
            }}
          >
            Crear un nuevo pedido
          </CustomButton>
        </div>
      </main>
    </>
  );
}
